import { FC, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { getErrorMessage } from '@/api/api-client'
import { useCreatePkgReq } from '@/api/package-req/mutations/useCreatePkgReq'
import { useRejectPkgReq } from '@/api/package-req/mutations/useRejectPkgReq'
import { PkgReqTypeEnum } from '@/api/package-req/types/PkgReq'
import { useCancelShipment } from '@/api/payments/mutations/useCancelShipment'
import { UserPkg } from '@/api/user-package/types/UserPkg'
import { AppTooltip } from '@/components/AppTooltip'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import FailIcon from '@/components/icons/fail.svg?react'
import PhotoDownloadIcon from '@/components/icons/photo-download.svg?react'
import PhotoIcon from '@/components/icons/pkg-photo.svg?react'
import ReturnIcon from '@/components/icons/pkg-return.svg?react'
import ShrinkIcon from '@/components/icons/pkg-shrink.svg?react'
import UniteIcon from '@/components/icons/pkg-unite.svg?react'
import { Button } from '@/components/ui/button'
import { getIsShrinkAllowed } from '@/helpers/pkgUtils'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/RoutePath'

import { PhotoListDialog } from '../../../../components/PhotoListDialog'
import { CancelPkgReqConfirmDialog } from './CancelPkgReqConfirmDialog'
import { PkgRequestConfirmDialog } from './PkgRequestConfirmDialog'

const ReqButton = ({
  IconComponent,
  label,
  onClick,
  disabled,
  tootlipContent,
}: {
  IconComponent: FC<React.SVGProps<SVGSVGElement>>
  label: string
  onClick?: () => void
  disabled?: boolean
  tootlipContent?: React.ReactNode
}) => {
  return (
    <AppTooltip triggerProps={{ asChild: true }} content={tootlipContent} asChild side='top'>
      <Button
        icon={<IconComponent className={cn('size-6', !disabled && 'text-icon-primary')} />}
        variant='secondary'
        onClick={disabled ? undefined : onClick}
        className={cn(
          'gap-2 text-xs disabled:pointer-events-auto disabled:cursor-default disabled:hover:shadow-none',
          disabled && 'text-text-disabled cursor-default bg-[#F7F8F9] hover:shadow-none'
        )}
      >
        {label}
      </Button>
    </AppTooltip>
  )
}
interface ReqButtonProps {
  pkg: UserPkg
  loadingCreateReq?: boolean
  onCreateReq: (reqType: PkgReqTypeEnum, comment?: string) => void
  onCancelReq: (reqType: PkgReqTypeEnum, pkgReqId: string) => void
}
const ShrinkButton = ({ pkg, onCreateReq, onCancelReq, loadingCreateReq }: ReqButtonProps) => {
  const { t } = useTranslation()
  const [isShrinkDialogOpen, setIsShrinkDialogOpen] = useState(false)
  const { showSuccessToast, showErrorToast } = useToast()

  const handleShrinkConfirm = async (comment: string) => {
    try {
      await onCreateReq(PkgReqTypeEnum.ShrinkRequest, comment)
      setIsShrinkDialogOpen(false)
      showSuccessToast(t('pkgReqs.shrinkSuccess'), t('pkgReqs.shrinkSuccessDesc'))
    } catch (error) {
      console.error('Error creating shrink request', error)
      showErrorToast(t('pkgReqs.shrinkError'), t('pkgReqs.shrinkErrorDesc'))
    }
  }

  if (pkg.isReturnRequestActive) {
    return <ReqButton IconComponent={ShrinkIcon} label={t('pkgReqs.shrink')} disabled tootlipContent={t('pkgReqs.returnInProgress')} />
  }
  if (pkg.isShrinkRequestApproved) {
    return <ReqButton IconComponent={ShrinkIcon} label={t('pkgReqs.shrink')} disabled tootlipContent={t('pkgReqs.shrinkDone')} />
  }
  if (pkg.isShrinkRequestActive) {
    return (
      <ReqButton
        IconComponent={ShrinkIcon}
        label={t('pkgReqs.cancelShrink')}
        onClick={() => onCancelReq(PkgReqTypeEnum.ShrinkRequest, pkg.shrinkRequestId!)}
      />
    )
  }

  const isShrinkAllowed = getIsShrinkAllowed(pkg)
  if (isShrinkAllowed) {
    return (
      <>
        <ReqButton IconComponent={ShrinkIcon} label={t('pkgReqs.shrink')} onClick={() => setIsShrinkDialogOpen(true)} />
        <PkgRequestConfirmDialog
          requestType={PkgReqTypeEnum.ShrinkRequest}
          isOpen={isShrinkDialogOpen}
          onClose={() => setIsShrinkDialogOpen(false)}
          onConfirm={handleShrinkConfirm}
          isLoading={loadingCreateReq}
        />
      </>
    )
  }
  return <ReqButton IconComponent={ShrinkIcon} label={t('pkgReqs.shrink')} disabled tootlipContent={t('pkgReqs.shrinkDoneFree')} />
}

const PhotoButton = ({ pkg, onCreateReq, onCancelReq, loadingCreateReq }: ReqButtonProps) => {
  const { t } = useTranslation()
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false)
  const [isPhotosGalleryOpen, setIsPhotosGalleryOpen] = useState(false)
  const { showSuccessToast, showErrorToast } = useToast()
  const handlePhotoConfirm = async (notes: string) => {
    try {
      await onCreateReq(PkgReqTypeEnum.PhotoRequest, notes)
      setIsPhotoDialogOpen(false)
      showSuccessToast(t('pkgReqs.photoSuccess'), t('pkgReqs.photoSuccessDesc'))
    } catch (error) {
      console.error('Error creating photo request', error)
      showErrorToast(t('pkgReqs.photoError'), t('pkgReqs.photoErrorDesc'))
    }
  }

  const photos = pkg.photos && pkg.photos.length > 0 ? pkg.photos : pkg.photo ? [pkg.photo] : []
  if (photos.length > 0) {
    return (
      <>
        <ReqButton IconComponent={PhotoDownloadIcon} label={t('pkgReqs.viewPhotos')} onClick={() => setIsPhotosGalleryOpen(true)} />
        <PhotoListDialog photos={photos} isOpen={isPhotosGalleryOpen} onOpenChange={setIsPhotosGalleryOpen} />
      </>
    )
  }
  if (pkg.isReturnRequestActive) {
    return <ReqButton IconComponent={PhotoIcon} label={t('pkgReqs.photo')} disabled tootlipContent={t('pkgReqs.returnInProgress')} />
  }
  if (pkg.isPhotoRequestActive) {
    return (
      <ReqButton
        IconComponent={PhotoIcon}
        label={t('pkgReqs.cancelPhoto')}
        onClick={() => onCancelReq(PkgReqTypeEnum.PhotoRequest, pkg.photoRequestId!)}
      />
    )
  }
  return (
    <>
      <ReqButton IconComponent={PhotoIcon} label={t('pkgReqs.photo')} onClick={() => setIsPhotoDialogOpen(true)} />
      <PkgRequestConfirmDialog
        requestType={PkgReqTypeEnum.PhotoRequest}
        isOpen={isPhotoDialogOpen}
        onClose={() => setIsPhotoDialogOpen(false)}
        onConfirm={handlePhotoConfirm}
        isLoading={loadingCreateReq}
      />
    </>
  )
}

const ReturnButton = ({ pkg, onCancelReq }: ReqButtonProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const handleCreateReturnReq = () => {
    navigate(`${RoutePath.ClientPkgReturn}/${pkg.id}`)
  }

  if (pkg.isShrinkRequestActive || pkg.isPhotoRequestActive || pkg.isUniteRequestActive) {
    return (
      <ReqButton IconComponent={ReturnIcon} label={t('pkgReqs.return')} disabled tootlipContent={t('pkgReqs.otherRequestInProgress')} />
    )
  }
  if (pkg.isReturnRequestActive && pkg.canCancelReturnRequest) {
    return (
      <ReqButton
        IconComponent={FailIcon}
        label={t('pkgReqs.cancelReturnToStore')}
        onClick={() => onCancelReq(PkgReqTypeEnum.ReturnRequest, pkg.returnRequestId!)}
      />
    )
  }
  return <ReqButton IconComponent={ReturnIcon} label={t('pkgReqs.return')} onClick={handleCreateReturnReq} />
}

const CancelShipmentButton = ({ pkg }: ReqButtonProps) => {
  const { t } = useTranslation()
  const { showSuccessToast, showErrorToast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const { mutateAsync: cancelShipment, isPending } = useCancelShipment()

  if (!pkg.canCancelShipment) return null

  const handleConfirm = async () => {
    try {
      await cancelShipment(pkg.id)
      setIsOpen(false)
      showSuccessToast(t('pkgReqs.cancelShipmentSuccess'), t('pkgReqs.cancelShipmentSuccessDesc'))
    } catch (err) {
      showErrorToast(t('pkgReqs.cancelShipmentError'), getErrorMessage(err))
    }
  }

  return (
    <>
      <ReqButton IconComponent={FailIcon} label={t('pkgReqs.cancelShipment')} onClick={() => setIsOpen(true)} />
      <ConfirmDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirm={handleConfirm}
        confirmLoading={isPending}
        title={t('pkgReqs.cancelShipmentTitle')}
        content={<Trans i18nKey='pkgReqs.cancelShipmentContent' />}
        confirmText={t('pkgReqs.cancelShipmentConfirm')}
        cancelText={t('pkgReqs.cancelShipmentBack')}
      />
    </>
  )
}
const UniteButton = ({ pkg, onCancelReq }: ReqButtonProps) => {
  const { t } = useTranslation()
  return (
    <ReqButton
      IconComponent={UniteIcon}
      label={t('pkgReqs.cancelUniteRequest')}
      onClick={() => onCancelReq(PkgReqTypeEnum.UniteRequest, pkg.uniteRequestId!)}
    />
  )
}
interface PkgReqMenuProps {
  pkg: UserPkg
  className?: string
}
export const PkgReqMenu = ({ pkg, className }: PkgReqMenuProps) => {
  const { t } = useTranslation()
  const { mutateAsync: createPkgReq, isPending: loadingCreateReq } = useCreatePkgReq()
  const { mutateAsync: rejectPkgReq, isPending: loadingRejectReq } = useRejectPkgReq()
  const { showSuccessToast, showErrorToast } = useToast()
  const [reqToCancel, setReqToCancel] = useState<{ reqType: PkgReqTypeEnum; pkgReqId: string } | null>(null)

  const handleRejectPkgReq = async () => {
    try {
      await rejectPkgReq({
        packageReqId: reqToCancel!.pkgReqId,
        rejectReason: 'userCanceled',
      })

      setReqToCancel(null)
      if (reqToCancel?.reqType === PkgReqTypeEnum.ReturnRequest) {
        showSuccessToast(t('pkgReqs.cancelReturnSuccess'), t('pkgReqs.cancelReturnSuccessDesc'))
      } else {
        showSuccessToast(t('pkgReqs.requestCanceledSuccess'))
      }
    } catch (err) {
      console.error('Error canceling package request', err)
      showErrorToast(t('pkgReqs.requestCancelErrorTitle'), t('pkgReqs.requestCancelErrorDesc'))
    }
  }
  const onCancelReq = async (reqType: PkgReqTypeEnum, pkgReqId: string) => {
    setReqToCancel({ reqType, pkgReqId })
  }
  const handleCreatePkgReq = async (reqType: PkgReqTypeEnum, comment?: string) => {
    await createPkgReq({
      packageId: pkg.id,
      reqType,
      userComment: comment,
    })
  }
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {pkg.isUniteRequestActive ? (
        <UniteButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
      ) : (
        <>
          <CancelShipmentButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
          {!pkg.isPaid && (
            <>
              <ShrinkButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
              <PhotoButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
              <ReturnButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
            </>
          )}
        </>
      )}
      <CancelPkgReqConfirmDialog
        isOpen={!!reqToCancel}
        onClose={() => setReqToCancel(null)}
        onConfirm={handleRejectPkgReq}
        actionType={reqToCancel?.reqType}
        isLoading={loadingRejectReq}
      />
    </div>
  )
}
